/**
 * ServiceWizardEditor — manage assistants (create / edit / duplicate / disable).
 *
 * The definition is edited as validated JSON. This keeps every wizard fully
 * editable so the catalogue never ages badly; a visual step editor can be
 * layered on top later without changing the data model.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, DocumentDuplicateIcon, WrenchScrewdriverIcon, CodeBracketIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import ServiceWizardVisualEditor from './ServiceWizardVisualEditor';
import './ServiceDesigner.css';

const BLANK = {
  key: '', label: '', icon: '🧩', service_type: 'other', description: '',
  definition: { steps: [], relations: [] },
};

export default function ServiceWizardEditor({ companyId, onClose }) {
  const [wizards, setWizards] = useState([]);
  const [sel, setSel] = useState(null);     // selected wizard (or draft)
  const [mode, setMode] = useState('visual'); // 'visual' | 'json'
  const [def, setDef] = useState({ steps: [], relations: [] });
  const [defText, setDefText] = useState('');
  const [defError, setDefError] = useState(null);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.swList(companyId);
      setWizards(r.data || []);
    } catch (e) { setError(e.message); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const hydrate = (w) => {
    const d = w.definition || { steps: [], relations: [] };
    setSel({ ...w });
    setDef(d);
    setDefText(JSON.stringify(d, null, 2));
    setMode('visual');
    setDefError(null); setError(null); setMsg(null);
  };

  const edit = (w) => hydrate(w);
  const newDraft = () => hydrate({ ...BLANK, isNew: true, definition: BLANK.definition });
  const duplicate = (w) => hydrate({
    ...w, id: undefined, isNew: true, is_system: false,
    key: `${w.key}_copia`, label: `${w.label} (copia)`,
  });

  const switchToJson = () => { setDefText(JSON.stringify(def, null, 2)); setDefError(null); setMode('json'); };
  const switchToVisual = () => {
    try { setDef(JSON.parse(defText)); setDefError(null); setMode('visual'); }
    catch (e) { setDefError(`JSON no válido: ${e.message}`); }
  };

  const save = async () => {
    let finalDef = def;
    if (mode === 'json') {
      try { finalDef = JSON.parse(defText); setDefError(null); }
      catch (e) { setDefError(`JSON no válido: ${e.message}`); return; }
    }
    if (!sel.label?.trim()) { setError('El nombre (label) es obligatorio.'); return; }
    setBusy(true); setError(null); setMsg(null);
    try {
      if (sel.isNew) {
        if (!sel.key?.trim()) { setError('La clave (key) es obligatoria.'); setBusy(false); return; }
        await api.swCreate(companyId, {
          key: sel.key.trim(), label: sel.label.trim(), icon: sel.icon,
          service_type: sel.service_type, description: sel.description, definition: finalDef,
        });
      } else {
        await api.swUpdate(companyId, sel.id, {
          label: sel.label.trim(), icon: sel.icon, service_type: sel.service_type,
          description: sel.description, definition: finalDef,
        });
      }
      setMsg('Guardado.');
      await load();
      setSel(null);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const remove = async (w) => {
    if (!window.confirm(`¿Eliminar/desactivar el asistente «${w.label}»?`)) return;
    try { await api.swDelete(companyId, w.id); await load(); if (sel?.id === w.id) setSel(null); }
    catch (e) { setError(e.message); }
  };

  const resync = async () => {
    if (!window.confirm('Actualizar los asistentes de sistema desde el catálogo base. Los asistentes propios NO se tocan. ¿Continuar?')) return;
    try {
      const r = await api.swResync(companyId);
      setMsg(`Actualizados: ${(r.data?.updated || []).join(', ') || 'ninguno'}`);
      await load();
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="sd-modal-backdrop" onClick={onClose}>
      <div className="sd-modal sd-model-modal sw-editor" onClick={(e) => e.stopPropagation()}>
        <div className="sd-model-head">
          <h3>🛠️ Asistentes — editor</h3>
          <button className="sd-model-x" onClick={onClose}><XMarkIcon width={16} height={16} /></button>
        </div>

        {error && <div className="sd-error-banner">{error}<button onClick={() => setError(null)}><XMarkIcon width={14} height={14} /></button></div>}
        {msg && <div className="sw-ok-banner">{msg}</div>}

        <div className="sw-editor-grid">
          <div className="sw-editor-list">
            <button className="sd-save-btn sw-full" onClick={newDraft}><PlusIcon width={15} height={15} /> Nuevo asistente</button>
            <button className="sd-cancel-btn sw-full" onClick={resync} title="Actualiza los asistentes de sistema a la base (los tuyos no se tocan, pero los de sistema editados SÍ vuelven a la semilla)">🔄 Sincronizar base</button>
            {wizards.map((w) => (
              <div key={w.id} className={`sw-editor-item ${sel?.id === w.id ? 'is-sel' : ''}`}>
                <button className="sw-editor-pick" onClick={() => edit(w)}>
                  <span>{w.icon} {w.label}</span>
                  <small>
                    {w.key}{w.is_system ? ' · sistema' : ''}{w.is_modified ? ' · modificado' : ''}
                  </small>
                </button>
                <div className="sw-editor-item-actions">
                  <button title="Duplicar" onClick={() => duplicate(w)}><DocumentDuplicateIcon width={14} height={14} /></button>
                  <button title="Eliminar" onClick={() => remove(w)}><TrashIcon width={14} height={14} /></button>
                </div>
              </div>
            ))}
          </div>

          <div className="sw-editor-form">
            {!sel ? (
              <div className="sw-search-hint">Elige un asistente de la izquierda o crea uno nuevo.</div>
            ) : (
              <>
                <div className="sw-editor-row">
                  <label className="sw-field"><span>Icono</span>
                    <input className="sd-builder-input" value={sel.icon || ''} maxLength={4}
                      onChange={(e) => setSel({ ...sel, icon: e.target.value })} />
                  </label>
                  <label className="sw-field sw-grow"><span>Nombre *</span>
                    <input className="sd-builder-input" value={sel.label || ''}
                      onChange={(e) => setSel({ ...sel, label: e.target.value })} />
                  </label>
                </div>
                <div className="sw-editor-row">
                  <label className="sw-field sw-grow"><span>Clave (key) *</span>
                    <input className="sd-builder-input" value={sel.key || ''} disabled={!sel.isNew}
                      onChange={(e) => setSel({ ...sel, key: e.target.value.replace(/[^a-z0-9_]/g, '') })} />
                  </label>
                  <label className="sw-field sw-grow"><span>Tipo de servicio</span>
                    <input className="sd-builder-input" value={sel.service_type || ''}
                      onChange={(e) => setSel({ ...sel, service_type: e.target.value })} />
                  </label>
                </div>
                <label className="sw-field"><span>Descripción</span>
                  <textarea className="sd-builder-input sd-builder-area" rows={2} value={sel.description || ''}
                    onChange={(e) => setSel({ ...sel, description: e.target.value })} />
                </label>
                {sel.is_modified && (
                  <div className="sw-warnings">
                    <div className="sw-warning">
                      ⚠ Modificado respecto a la base: el refresco automático ya no lo pisa (tus cambios se
                      guardan). «Sincronizar base» sí lo devolvería a la semilla.
                    </div>
                  </div>
                )}
                <div className="vw-mode">
                  <button type="button" className={mode === 'visual' ? 'is-on' : ''} onClick={switchToVisual}>
                    <WrenchScrewdriverIcon width={14} height={14} /> Editor visual
                  </button>
                  <button type="button" className={mode === 'json' ? 'is-on' : ''} onClick={switchToJson}>
                    <CodeBracketIcon width={14} height={14} /> JSON
                  </button>
                  <span className="vw-mode-hint">
                    {mode === 'visual' ? 'Pasos, preguntas y relaciones con controles.' : 'Edición avanzada del documento completo.'}
                  </span>
                </div>

                {mode === 'visual' ? (
                  <ServiceWizardVisualEditor key={sel.id || 'draft'} definition={def} onChange={setDef} />
                ) : (
                  <label className="sw-field"><span>Definición (JSON: steps + relations)</span>
                    <textarea className="sd-builder-input sw-json" rows={18} spellCheck={false} value={defText}
                      onChange={(e) => { setDefText(e.target.value); setDefError(null); }} />
                  </label>
                )}
                {defError && <div className="sd-error-banner">{defError}</div>}
                <div className="sd-modal-actions">
                  <button className="sd-cancel-btn" onClick={() => setSel(null)}>Cancelar</button>
                  <button className="sd-save-btn" disabled={busy} onClick={save}>{busy ? 'Guardando…' : 'Guardar'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
